import axios from 'axios'
import { ref } from 'vue'
import type { OAuthClient, OAuthToken, CreateTokenResponse } from '../types'

/**
 * Composable for OAuth API operations (clients and tokens)
 *
 * @returns Object with reactive state and API methods for OAuth management
 */
export function useOAuthClients() {
  const clients = ref<OAuthClient[]>([])
  const isLoading = ref(false)

  async function fetchClients(): Promise<void> {
    isLoading.value = true
    try {
      const response = await axios.get<OAuthClient[]>('/oauth/clients')
      clients.value = response.data
    } finally {
      isLoading.value = false
    }
  }

  async function createClient(name: string, redirect: string): Promise<OAuthClient> {
    isLoading.value = true
    try {
      const response = await axios.post<OAuthClient>('/oauth/clients', {
        name,
        redirect,
      })
      clients.value.push(response.data)
      return response.data
    } finally {
      isLoading.value = false
    }
  }

  async function revokeClient(clientId: string): Promise<void> {
    await axios.delete(`/oauth/clients/${clientId}`)
    await fetchClients()
  }

  return {
    clients,
    isLoading,
    fetchClients,
    createClient,
    revokeClient,
  }
}

/**
 * Composable for OAuth personal access token operations
 *
 * @returns Object with reactive state and API methods for token management
 */
export function useOAuthTokens() {
  const tokens = ref<OAuthToken[]>([])
  const isLoading = ref(false)

  async function fetchTokens(): Promise<void> {
    isLoading.value = true
    try {
      const response = await axios.get<OAuthToken[]>('/oauth/personal-access-tokens')
      tokens.value = response.data
    } finally {
      isLoading.value = false
    }
  }

  async function createToken(name: string): Promise<CreateTokenResponse> {
    isLoading.value = true
    try {
      const response = await axios.post<CreateTokenResponse>('/oauth/personal-access-tokens', {
        name,
      })
      await fetchTokens()
      return response.data
    } finally {
      isLoading.value = false
    }
  }

  async function revokeToken(tokenId: string): Promise<void> {
    await axios.delete(`/oauth/personal-access-tokens/${tokenId}`)
    await fetchTokens()
  }

  return {
    tokens,
    isLoading,
    fetchTokens,
    createToken,
    revokeToken,
  }
}
